import { useState, useCallback } from 'react';
import { IPHEPlateType } from '../../../Models/IPHEPlateType';
import { AvailableProductLine } from '../../../Models/Enums';

// Component Props Interface
export interface PHEPlateTypeManagementProps {
  // No props needed for this component currently
}

// Column interface for Material UI table
export interface Column {
  id: string;
  label: string;
  minWidth: number;
  maxWidth: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, item?: IPHEPlateType, index?: number) => React.ReactNode;
  sortable?: boolean;
}

// State interface for the component
export interface PHEPlateTypeManagementState {
  // Modal state
  isModalOpen: boolean;
  pheTypeActive?: IPHEPlateType;
  
  // Data state
  currentData: IPHEPlateType[];
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: string;
  showDeleteConfirmation: string;
  searchTerm: string;
  
  // Sorting state
  order: 'asc' | 'desc';
  orderBy: string;
  
  // Form state
  availablePositions: string[];
  masterListOfAvailablePositions: string[];
  selectedPositions: string[];
  disabledForPositions: any[];
  text: string;
  key: string;
  infoForALICE: string;
  allowedPositionTexts: string[];
  dataColumns: Column[];
  availableInALICE: boolean;
  industrial: boolean;
  hygienic: boolean;
  unitConfigData: any[];
}

/**
 * Custom hook for managing PHEPlateTypeManagement state
 */
export const usePHEPlateTypeManagementState = () => {
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [pheTypeActive, setActivePHEType] = useState<IPHEPlateType>();

  // Data state
  const [currentData, setCurrentData] = useState<IPHEPlateType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Sorting state
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('text');

  // Form state
  const [availablePositions, setAvailablePositions] = useState<string[]>([]);
  const [masterListOfAvailablePositions, setMasterListOfAvailablePositions] = useState<string[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [disabledForPositions, setDisabledForPositions] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [key, setKey] = useState('');
  const [infoForALICE, setInfoForALICE] = useState('');
  const [allowedPositionTexts, setAllowedTexts] = useState<string[]>([]);
  const [dataColumns, setDataColumns] = useState<Column[]>([]);
  const [availableInALICE, setAvailableInALICE] = useState(false);
  const [industrial, setIndustrial] = useState(false);
  const [hygienic, setHygienic] = useState(false);
  const [unitConfigData, setUnitConfigData] = useState<any[]>([]);

  /**
   * Reset form state - memoized to prevent re-creation
   */
  const resetFormState = useCallback(() => {
    setActivePHEType(undefined);
    setText('');
    setKey('');
    setSelectedPositions([]);
    setInfoForALICE('');
    setAvailableInALICE(false);
    setIndustrial(false);
    setHygienic(false);
    setIsSaving(false);
  }, []);

  /**
   * Reset modal and form state - memoized to prevent re-creation
   */
  const resetModalState = useCallback(() => {
    resetFormState();
    setAvailablePositions(masterListOfAvailablePositions);
    setAllowedTexts(masterListOfAvailablePositions);
    setIsModalOpen(false);
  }, [masterListOfAvailablePositions, resetFormState]);

  /**
   * Open modal - memoized to prevent re-creation
   */
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  /**
   * Handle position selection logic - memoized to prevent re-creation
   */
  const handlePositionSelection = useCallback((item: string, selected: boolean) => {
    if (selected) {
      setSelectedPositions(prev => [...prev, item]);
      setAvailablePositions(prev => prev.filter((pos: string) => pos !== item));
    } else {
      setSelectedPositions(prev => prev.filter((pos: string) => pos !== item));
      setAvailablePositions(prev => [...prev, item]);
    }
  }, []);

  /**
   * Check if form can be submitted - memoized to prevent re-creation
   */
  const isSubmitDisabled = useCallback(() => {
    // For new PHE types: require text and key
    if (pheTypeActive === undefined) {
      return text === '' || key === '';
    }
    
    // For editing PHE types: check if any field changed
    const positionsChanged = JSON.stringify(allowedPositionTexts.sort()) !== JSON.stringify(availablePositions.sort());
    const textChanged = text !== pheTypeActive.text;
    const availableInALICEChanged = availableInALICE !== pheTypeActive.isAvailableInALICE;
    const infoForALICEChanged = infoForALICE !== (pheTypeActive.informationForUser || '');
    
    // Check if productLine changed
    let productLineChanged = false;
    if (industrial && pheTypeActive.availableProductLine !== AvailableProductLine.Industrial) {
      productLineChanged = true;
    } else if (hygienic && pheTypeActive.availableProductLine !== AvailableProductLine.Hygienic) {
      productLineChanged = true;
    } else if (!industrial && !hygienic && pheTypeActive.availableProductLine !== null) {
      productLineChanged = true;
    }
    
    // Enable submit if any field changed
    const hasChanges = positionsChanged || textChanged || availableInALICEChanged || infoForALICEChanged || productLineChanged;
    
    return !hasChanges;
  }, [pheTypeActive, text, key, allowedPositionTexts, availablePositions, availableInALICE, infoForALICE, industrial, hygienic]);

  /**
   * Handle Industrial checkbox logic - memoized to prevent re-creation
   */
  const handleIndustrialChange = useCallback(() => {
    setIndustrial(prev => !prev);
    setHygienic(false); // Uncheck the other checkbox
  }, []);

  /**
   * Handle Hygienic checkbox logic - memoized to prevent re-creation
   */
  const handleHygienicChange = useCallback(() => {
    setHygienic(prev => !prev);
    setIndustrial(false); // Uncheck the other checkbox
  }, []);

  return {
    // Modal state
    isModalOpen,
    setIsModalOpen,
    pheTypeActive,
    setActivePHEType,
    
    // Data state
    currentData,
    setCurrentData,
    isLoading,
    setIsLoading,
    isSaving,
    setIsSaving,
    isDeleting,
    setIsDeleting,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    searchTerm,
    setSearchTerm,
    
    // Sorting state
    order,
    setOrder,
    orderBy,
    setOrderBy,
    
    // Form state
    availablePositions,
    setAvailablePositions,
    masterListOfAvailablePositions,
    setMasterListOfAvailablePositions,
    selectedPositions,
    setSelectedPositions,
    disabledForPositions,
    setDisabledForPositions,
    text,
    setText,
    key,
    setKey,
    infoForALICE,
    setInfoForALICE,
    allowedPositionTexts,
    setAllowedTexts,
    dataColumns,
    setDataColumns,
    availableInALICE,
    setAvailableInALICE,
    industrial,
    setIndustrial,
    hygienic,
    setHygienic,
    unitConfigData,
    setUnitConfigData,
    
    // Helper methods
    resetFormState,
    resetModalState,
    openModal,
    handlePositionSelection,
    isSubmitDisabled,
    handleIndustrialChange,
    handleHygienicChange
  };
}; 